import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { testimonials } from '../data';
import { Quote, ChevronLeft, ChevronRight, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

const Referencer = () => {
  const [selectedTestimonialIndex, setSelectedTestimonialIndex] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);

  const clientTestimonials = testimonials.filter(t => t.type === 'klient');
  const businessTestimonials = testimonials.filter(t => t.type === 'erhverv' || t.type === 'foredrag');

  const openModal = (index, category) => {
    setSelectedTestimonialIndex(index);
    setCurrentCategory(category);
  };

  const closeModal = () => {
    setSelectedTestimonialIndex(null);
    setCurrentCategory(null);
  };

  const navigate = (direction) => {
    const list = currentCategory === 'clients' ? clientTestimonials : businessTestimonials;
    let newIndex = selectedTestimonialIndex + direction;
    if (newIndex < 0) newIndex = list.length - 1;
    if (newIndex >= list.length) newIndex = 0;
    setSelectedTestimonialIndex(newIndex);
  };

  const activeTestimonial = selectedTestimonialIndex !== null 
    ? (currentCategory === 'clients' ? clientTestimonials[selectedTestimonialIndex] : businessTestimonials[selectedTestimonialIndex])
    : null;

  const truncateText = (text, maxLength = 240) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
  };

  return (
    <div className="bg-white min-h-screen">
      <section className="py-12 bg-zinc-50 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-6">Referencer & <span className="text-emerald-600">Udtalelser</span></h1>
            <p className="text-xl text-slate-600">
              Tillid er fundamentet for alt hvad jeg gør. Her kan du læse om de erfaringer, mine klienter og samarbejdspartnere har haft.
            </p>
          </div>
        </div>
      </section>

      {/* Fremhævede Video-anbefalinger */}
      <section className="py-16 md:py-20 bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-3.5 py-1.5 rounded-full inline-block mb-3">
              Videoudtalelser
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 flex items-center">
              <span className="w-8 h-1 bg-emerald-600 mr-4"></span>
              Hør hvad samarbejdspartnerne siger
            </h2>
            <p className="text-slate-600 text-lg mt-3">
              Få et levende indblik i værdien af et samarbejde med Michael – direkte fra store erhvervskunder.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Allan - Logistikkompagniet */}
            <div className="bg-zinc-50 rounded-3xl border border-zinc-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="relative aspect-video bg-zinc-950 overflow-hidden">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov"
                >
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov" type="video/quicktime" />
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov" type="video/mp4" />
                  Din browser understøtter ikke video-tagget.
                </video>
              </div>
              <div className="p-6 sm:p-8 flex flex-col flex-grow justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/70 px-3 py-1 rounded-full">
                      Logistikkompagniet
                    </span>
                    <span className="text-xs text-slate-500 font-medium">Video-anbefaling</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3 leading-snug">
                    "Værdien af det længerevarende samarbejde"
                  </h3>
                  <div className="relative mb-6">
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed italic">
                      Allan deler sine erfaringer med et længerevarende samarbejde, hvor Michael er en aktiv del af hverdagen, deltager i møder og skaber tryghed, trivsel og stærk dynamik i organisationen.
                    </p>
                  </div>
                </div>
                <div className="pt-5 border-t border-zinc-200/80">
                  <h4 className="font-bold text-zinc-900 text-base">Allan</h4>
                  <p className="text-sm text-emerald-700 font-medium">Logistikkompagniet</p>
                </div>
              </div>
            </div>

            {/* Bahadir Demirhan - Nybolig Odense */}
            <div className="bg-zinc-50 rounded-3xl border border-zinc-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="relative aspect-video bg-zinc-950 overflow-hidden">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/459b022c-0129-44c7-b9de-c19d36d80dc6.mp4"
                >
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/459b022c-0129-44c7-b9de-c19d36d80dc6.mp4" type="video/mp4" />
                  Din browser understøtter ikke video-tagget.
                </video>
              </div>
              <div className="p-6 sm:p-8 flex flex-col flex-grow justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/70 px-3 py-1 rounded-full">
                      Nybolig Odense
                    </span>
                    <span className="text-xs text-slate-500 font-medium">Video-anbefaling</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3 leading-snug">
                    "Blot efter få timer var vi klædt på med nye redskaber"
                  </h3>
                  <div className="relative mb-6">
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed italic">
                      Nybolig Odense fik hjælp af Michael til at styrke empati og relationer. Efter blot få timer var hele mæglerteamet klædt på med enkle og konkrete redskaber, der med det samme gjorde en mærkbar forskel i hverdagen.
                    </p>
                  </div>
                </div>
                <div className="pt-5 border-t border-zinc-200/80">
                  <h4 className="font-bold text-zinc-900 text-base">Bahadir Demirhan</h4>
                  <p className="text-sm text-emerald-700 font-medium">Indehaver &amp; Ejendomsmægler · Nybolig Odense (Thomsen, Demirhan &amp; Led)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Clients Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-zinc-900 mb-12 flex items-center">
            <span className="w-8 h-1 bg-emerald-600 mr-4"></span>
            Klienter
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-50 p-8 border border-zinc-100 flex flex-col h-full hover:shadow-md transition-shadow group"
              >
                <Quote className="h-8 w-8 text-emerald-900/10 mb-6" />
                <div className="flex-grow">
                  <p className="text-slate-600 italic leading-relaxed mb-4 whitespace-pre-line">
                    "{truncateText(testimonial.text)}"
                  </p>
                  {testimonial.text.length > 240 && (
                    <button 
                      onClick={() => openModal(index, 'clients')}
                      className="text-emerald-600 font-semibold text-sm hover:text-emerald-700 transition-colors mb-8 inline-flex items-center gap-1"
                    >
                      Læs mere
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="pt-6 border-t border-zinc-200">
                  <h4 className="font-bold text-zinc-900">{testimonial.name}</h4>
                  {testimonial.age && (
                    <p className="text-sm text-emerald-700">{testimonial.age}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business & Lectures Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-zinc-900 mb-12 flex items-center">
            <span className="w-8 h-1 bg-emerald-600 mr-4"></span>
            Erhverv & Foredrag
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {businessTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-10 border border-zinc-200 flex flex-col h-full hover:shadow-lg transition-shadow group"
              >
                <Quote className="h-10 w-10 text-emerald-900/10 mb-6" />
                <div className="flex-grow">
                  <p className="text-lg text-slate-600 italic leading-relaxed mb-4 whitespace-pre-line">
                    "{truncateText(testimonial.text, 350)}"
                  </p>
                  {testimonial.text.length > 350 && (
                    <button 
                      onClick={() => openModal(index, 'business')}
                      className="text-emerald-600 font-semibold text-base hover:text-emerald-700 transition-colors mb-8 inline-flex items-center gap-1"
                    >
                      Læs mere
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className="pt-6 border-t border-zinc-100">
                  <h4 className="font-bold text-zinc-900 text-lg">{testimonial.name}</h4>
                  {testimonial.role && (
                    <p className="text-emerald-700 font-medium">{testimonial.role}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Modal */}
      <Dialog.Root open={selectedTestimonialIndex !== null} onOpenChange={(open) => !open && closeModal()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-zinc-900/80 backdrop-blur-sm z-[100] transition-opacity" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-4xl max-h-[90vh] bg-white z-[101] overflow-hidden flex flex-col shadow-2xl">
            <Dialog.Title className="sr-only">
              Udtalelse fra {activeTestimonial?.name}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              Læs den fulde udtalelse fra {activeTestimonial?.name} her.
            </Dialog.Description>
            <div className="relative p-6 md:p-12 overflow-y-auto flex-grow">
              <Dialog.Close className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-zinc-900 transition-colors">
                <X className="w-6 h-6" />
              </Dialog.Close>
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTestimonial?.id}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col"
                >
                  <Quote className="h-12 w-12 text-emerald-900/10 mb-8" />
                  <p className="text-lg md:text-xl text-slate-600 italic leading-relaxed mb-10 whitespace-pre-line">
                    "{activeTestimonial?.text}"
                  </p>
                  <div className="pt-8 border-t border-zinc-100">
                    <h4 className="text-xl font-bold text-zinc-900">{activeTestimonial?.name}</h4>
                    {activeTestimonial?.age && (
                      <p className="text-emerald-700 font-medium">{activeTestimonial?.age}</p>
                    )}
                    {activeTestimonial?.role && (
                      <p className="text-emerald-700 font-medium">{activeTestimonial?.role}</p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Bar */}
            <div className="bg-zinc-50 border-t border-zinc-100 p-4 flex justify-between items-center px-6 md:px-12">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 font-medium transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                Forrige
              </button>
              <div className="text-zinc-400 text-sm font-medium">
                {selectedTestimonialIndex + 1} / {currentCategory === 'clients' ? clientTestimonials.length : businessTestimonials.length}
              </div>
              <button
                onClick={() => navigate(1)}
                className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 font-medium transition-colors"
              >
                Næste
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-950 p-12 md:p-16 text-center text-white">
            <h2 className="text-3xl font-bold mb-6">Vil du være min næste succeshistorie?</h2>
            <p className="text-emerald-50/70 text-xl mb-10 max-w-2xl mx-auto">
              Uanset om du søger personlig vækst eller organisationsudvikling, hjælper jeg dig med at finde vejen frem.
            </p>
            <a
              href="/kontakt"
              className="inline-block bg-white text-emerald-900 px-10 py-5 font-bold text-xl hover:bg-emerald-50 transition-all"
            >
              Start dit forløb i dag
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Referencer;
